import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export default function CookiePolicyPage() {
  const { language } = useLanguage();
  const isLV = language === 'lv';

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="cookie-policy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {isLV ? 'Atpakaļ uz sākumu' : 'Back to home'}
        </Link>

        <div className="bg-white rounded-2xl border border-[#E5E4E0] p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#D97736] flex items-center justify-center">
              <Cookie className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#121212]">
                {isLV ? 'Sīkdatņu politika' : 'Cookie Policy'}
              </h1>
              <p className="text-sm text-[#5C5C5C]">
                {isLV ? 'Spēkā no 2026. gada' : 'Effective 2026'}
              </p>
            </div>
          </div>

          <article className="prose prose-sm max-w-none text-[#2B2B2B] leading-relaxed space-y-5">
            {isLV ? (
              <>
                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">1. Kas ir sīkdatnes?</h2>
                  <p>
                    Sīkdatnes (cookies) ir mazi teksta faili, ko vietne saglabā Tavā pārlūkā, lai
                    atcerētos darbības un uzlabotu lietošanu. Mēs izmantojam tikai tās sīkdatnes,
                    kas ir nepieciešamas, vai ar Tavu piekrišanu.
                  </p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">2. Kādas sīkdatnes lietojam</h2>

                  <h3 className="font-outfit text-base font-semibold text-[#121212] mt-3">
                    A. Nepieciešamās (vienmēr aktīvas)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border border-[#E5E4E0] rounded-lg overflow-hidden">
                      <thead className="bg-[#FAFAFA]">
                        <tr>
                          <th className="text-left p-2 font-medium">Nosaukums</th>
                          <th className="text-left p-2 font-medium">Mērķis</th>
                          <th className="text-left p-2 font-medium">Glabāšanas laiks</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-[#E5E4E0]">
                          <td className="p-2 font-mono">access_token</td>
                          <td className="p-2">Autorizācijas sesija</td>
                          <td className="p-2">1 stunda</td>
                        </tr>
                        <tr className="border-t border-[#E5E4E0]">
                          <td className="p-2 font-mono">refresh_token</td>
                          <td className="p-2">Sesijas atjaunošana</td>
                          <td className="p-2">7 dienas</td>
                        </tr>
                        <tr className="border-t border-[#E5E4E0]">
                          <td className="p-2 font-mono">kolv_cookie_consent_v1</td>
                          <td className="p-2">Tavas sīkdatņu izvēles saglabāšana</td>
                          <td className="p-2">12 mēneši</td>
                        </tr>
                        <tr className="border-t border-[#E5E4E0]">
                          <td className="p-2 font-mono">__cf_bm</td>
                          <td className="p-2">Cloudflare botu aizsardzība</td>
                          <td className="p-2">30 minūtes</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h3 className="font-outfit text-base font-semibold text-[#121212] mt-4">
                    B. Analītiskās (tikai ar piekrišanu)
                  </h3>
                  <p>
                    Anonīma statistika par vietnes apmeklētību un ceļojumiem — kuras lapas tiek
                    apskatītas, cik ilgi, no kādām ierīcēm. Piemēram, Google Analytics (_ga, _gid).
                    Aktivizējas tikai pēc Tavas piekrišanas.
                  </p>

                  <h3 className="font-outfit text-base font-semibold text-[#121212] mt-4">
                    C. Mārketinga (tikai ar piekrišanu)
                  </h3>
                  <p>
                    Reklāmas efektivitātes mērīšana (piem., Meta Pixel, Google Ads). Aktivizējas tikai
                    pēc Tavas piekrišanas. Šobrīd šīs sīkdatnes <strong>nav aktīvas</strong> — tiks
                    ieslēgtas nākotnē.
                  </p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">3. Kā pārvaldīt savu izvēli</h2>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Pirmajā apmeklējumā parādās sīkdatņu banneris — izvēlies “Tikai nepieciešamās”, “Piekrītu visām” vai “Iestatījumi”.</li>
                    <li>Lai mainītu izvēli vēlāk — izdzēs pārlūka sīkdatni <code>kolv_cookie_consent_v1</code>, tad banneris parādīsies no jauna.</li>
                    <li>Tavs pārlūks arī ļauj bloķēt visas sīkdatnes (iestatījumi → privātums). Piezīme: bloķējot nepieciešamās, vietne nedarbosies pareizi.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">4. Trešo pušu sīkdatnes</h2>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Stripe</strong> — maksājumu veikšanai (tikai maksājuma lapā).</li>
                    <li><strong>Cloudflare</strong> — DDoS aizsardzībai.</li>
                    <li><strong>Google Analytics</strong> (tikai ar piekrišanu) — statistikai.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">5. Izmaiņas</h2>
                  <p>
                    Par būtiskām izmaiņām informēsim vietnē. Jaunā sīkdatņu politika stāsies spēkā pēc
                    paziņojuma parādīšanas.
                  </p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">6. Kontakti</h2>
                  <p>
                    SIA “Gatis” (reģ.nr. 50203491851)<br />
                    Toma iela 7 - 44, Liepāja, LV-3401<br />
                    Jautājumi: <strong>kolatvietim@gmail.com</strong>
                  </p>
                </section>
              </>
            ) : (
              <p className="text-[#5C5C5C]">
                English version available on request. Please see the Latvian version above for full details.
              </p>
            )}
          </article>
        </div>
      </div>
    </div>
  );
}
